import {Inject, Injectable} from '@angular/core';
import {HttpClient} from "@angular/common/http";
import {Observable} from "rxjs";
import {LegalAccountState} from "../../legal/_models/_enums/legal-account-state";
import {ServerEndpoints} from "../../../server.endpoints";
import {ShardDto} from "../../../models/admin_owner/shard/shard-dto";

/**
 * Which source answered the managing-organisation lookup.
 *
 * `ManagingOrganizationResolver` asks the `fiskaly_managing_organization_id` setting first and only then falls
 * back to the legacy protected company. The distinction is worth carrying to the screen: `"legacy"` is a real,
 * working installation that is nevertheless one deleted company row away from refusing every shard
 * registration, and the only thing that fixes it is an operator typing the id into the setting.
 */
export type ManagingOrganizationSource = 'setting' | 'legacy' | 'none';

/** What `/Management/Fiskaly/{Get,Set}ManagingOrganization` answers. */
export interface ManagingOrganization {
  /** The organisation id currently in force, or `null` when neither source could name one. */
  organizationId: string | null;
  /** Which source it came from. `"setting"` after a successful set, by construction. */
  source: ManagingOrganizationSource;
}

/**
 * The fiskaly management API, as the platform-owner screens reach it.
 *
 * Two things only: the installation's own fiskaly credentials (Owner → **Integrations**) and the per-**shard**
 * registration (Owner → **Shards**) — both navigation entries read exactly that, in English, on the
 * platform-owner shell. There is deliberately no company-level call — a customer company has no object
 * on the fiskaly management API, and the one top-level organisation an installation has is created by the
 * operator in the fiskaly dashboard, not from here. See
 * `docs/articles/platform/fiskaly-managing-organization.md`.
 */
@Injectable({
  providedIn: 'root'
})
export class FiskalyHttpService {
  constructor(@Inject("BASE_URL") private baseUrl: string, private httpClient: HttpClient) {
  }

  /**
   * Reads the platform's managing organisation id and the source that answered.
   *
   * Separate from {@link isConfigured} on purpose: the credentials and the organisation id are two independent
   * failures (correct credentials with no organisation id still refuse every shard registration), so the card
   * reports them as two states rather than folding them into one "fiskaly is fine" verdict.
   */
  getManagingOrganization(): Observable<ManagingOrganization> {
    return this.httpClient.get<ManagingOrganization>(
      `${this.baseUrl}${ServerEndpoints.Management_Fiskaly_GetManagingOrganization}`);
  }

  /**
   * Records the managing organisation id. The server trims it and refuses a blank one with
   * `E_LEGAL_MANAGING_ORGANIZATION_ID_BLANK`; the answer is the same model {@link getManagingOrganization}
   * returns, whose `source` is then `"setting"`.
   */
  setManagingOrganization(organizationId: string): Observable<ManagingOrganization> {
    return this.httpClient.put<ManagingOrganization>(
      `${this.baseUrl}${ServerEndpoints.Management_Fiskaly_SetManagingOrganization}`, {organizationId});
  }

  isConfigured(): Observable<LegalAccountState> {
    return this.httpClient.get<LegalAccountState>(`${this.baseUrl}${ServerEndpoints.Management_Fiskaly_IsConfigured}`);
  }

  setCredentials(key: string, secret: string): Observable<LegalAccountState> {
    return this.httpClient.put<LegalAccountState>(`${this.baseUrl}${ServerEndpoints.Management_Fiskaly_SetCredentials}`, {
      name: key,
      password: secret
    })
  }

  upsertShard(id: string): Observable<ShardDto> {
    return this.httpClient.post<ShardDto>(`${this.baseUrl}${ServerEndpoints.Management_Fiskaly_UpsertShard_$id$.dictReplace({id: id})}`, {});
  }
}
